/**
 * ResetPassword — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Route: `/reset-password/:token`. Presentation migrated to
 * `@/design-system` primitives + `--ds-*` tokens. All validation rules,
 * API endpoints, testids, and redirect flow preserved verbatim.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { KeyRound, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AuthShell from "./AuthShell";
import { Button, Input, Card, CardBody } from "@/design-system";
import { FormField } from "./Login";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [phase, setPhase] = useState("loading");
  const [reason, setReason] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/password-reset/${encodeURIComponent(token)}`, {
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404 || s === 410,
        });
        if (cancelled) return;
        if (!data?.valid) {
          setReason(data?.reason || "This password reset link is no longer valid.");
          setPhase("invalid");
        } else {
          setEmailMasked(data.email_masked || "");
          setPhase("form");
        }
      } catch (e) {
        if (cancelled) return;
        setReason(formatApiError(e));
        setPhase("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  function strengthError() {
    if (pw.length < 8) return "Password must be at least 8 characters.";
    if (!/\d/.test(pw)) return "Password must contain at least one number.";
    if (pw !== pw2) return "Passwords don't match.";
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    const err = strengthError();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setPhase("done");
      try { await refresh(); } catch { /* ignored */ }
      setTimeout(() => navigate("/dashboard", { replace: true }), 1400);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  const titleMap = {
    loading: "Verifying your reset link…",
    invalid: "Link no longer valid.",
    form: "Choose a new password.",
    done: "Password updated.",
  };

  return (
    <AuthShell testId="reset-password-page" title={titleMap[phase] || "Reset password"}>
      {phase === "loading" && (
        <Card data-testid="reset-loading">
          <CardBody className="p-6 text-center text-[13px] text-[var(--ds-color-ink-muted)]">
            Contacting our servers…
          </CardBody>
        </Card>
      )}
      {phase === "invalid" && (
        <Card data-testid="reset-invalid">
          <CardBody className="p-6 md:p-8">
            <AlertCircle
              className="w-7 h-7 mb-3"
              style={{ color: "var(--ds-color-danger)" }}
              aria-hidden
            />
            <p className="text-[15px] mb-5 text-[var(--ds-color-ink-muted)]">{reason}</p>
            <Link
              to="/forgot-password"
              data-testid="reset-request-new"
              className="text-[13px] font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
            >
              Request a fresh reset link
            </Link>
          </CardBody>
        </Card>
      )}
      {phase === "form" && (
        <Card>
          <CardBody className="p-6 md:p-8">
            <form onSubmit={submit} className="space-y-4">
              <KeyRound
                className="w-6 h-6"
                style={{ color: "var(--ds-color-accent)" }}
                aria-hidden
              />
              {emailMasked && (
                <p className="text-[13px] text-[var(--ds-color-ink-muted)]">
                  Resetting password for{" "}
                  <strong className="text-[var(--ds-color-ink)]">{emailMasked}</strong>
                </p>
              )}
              <FormField label="New password" htmlFor="reset-pw">
                <div className="relative">
                  <Input
                    id="reset-pw"
                    data-testid="reset-pw"
                    type={show ? "text" : "password"}
                    required
                    
                    autoFocus
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Min 8 chars, 1 number"
                    className="pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    tabIndex={-1}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-2 flex items-center justify-center px-2 text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] rounded-[var(--ds-radius-sm)]"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </FormField>
              <FormField label="Confirm password" htmlFor="reset-pw2">
                <Input
                  id="reset-pw2"
                  data-testid="reset-pw2"
                  type={show ? "text" : "password"}
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                />
              </FormField>
              <Button
                data-testid="reset-submit"
                type="submit"
                loading={busy}
                disabled={busy || !pw || !pw2}
                className="w-full"
                size="lg"
              >
                {busy ? "Updating…" : "Reset password"}
              </Button>
              <p className="text-[13px] text-center leading-snug text-[var(--ds-color-ink-subtle)]">
                Any other active sessions will be signed out for security.
              </p>
              <div className="text-[13px] text-center pt-2 border-t border-[var(--ds-color-border)] text-[var(--ds-color-ink-muted)]">
                <Link
                  to="/login"
                  data-testid="reset-back-to-login"
                  className="font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
      {phase === "done" && (
        <Card data-testid="reset-done">
          <CardBody className="p-6 md:p-8">
            <CheckCircle2
              className="w-7 h-7 mb-3"
              style={{ color: "var(--ds-color-success)" }}
              aria-hidden
            />
            <p className="text-[15px] text-[var(--ds-color-ink-muted)]">
              Redirecting to your dashboard…
            </p>
          </CardBody>
        </Card>
      )}
    </AuthShell>
  );
}
